import axios from "axios";

const sanitizeFilenamePart = (value) =>
	String(value || "")
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "_")
		.replace(/^_+|_+$/g, "");

/** e.g. timothy_masiko_unit4_tenancy_agreement.pdf */
export const buildTenantAgreementFilename = ({
	firstName,
	lastName,
	unitName,
	signed = false,
}) => {
	const namePart =
		[firstName, lastName].map(sanitizeFilenamePart).filter(Boolean).join("_") || "tenant";
	const unitPart = sanitizeFilenamePart(unitName) || "unit";
	const signedPart = signed ? "_signed" : "";
	return `${namePart}_${unitPart}${signedPart}_tenancy_agreement.pdf`;
};

const withDownloadParam = (url) => {
	if (!url) return url;
	const separator = url.includes("?") ? "&" : "?";
	return `${url}${separator}download=1`;
};

const fetchPdfBlob = async (url, token) => {
	const response = await axios.get(url, {
		responseType: "blob",
		headers: token ? { Authorization: `Bearer ${token}` } : {},
	});
	return new Blob([response.data], { type: "application/pdf" });
};

const pdfErrorMessage = (error, fallback) =>
	error?.response?.data?.error ||
	error?.response?.data?.message ||
	error?.message ||
	fallback;

/** Open a JWT-protected API PDF in a new tab (tenant/landlord agreement streams). */
export const openAuthPdf = async (url, token) => {
	if (!url) return { success: false, message: "No PDF URL available." };
	try {
		const blob = await fetchPdfBlob(url, token);
		const blobUrl = URL.createObjectURL(blob);
		window.open(blobUrl, "_blank", "noopener,noreferrer");
		setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
		return { success: true };
	} catch (error) {
		return { success: false, message: pdfErrorMessage(error, "Failed to open PDF.") };
	}
};

/** Download a JWT-protected API PDF with a custom filename. */
export const downloadAuthPdf = async (url, token, filename) => {
	if (!url) return { success: false, message: "No PDF URL available." };
	try {
		const blob = await fetchPdfBlob(withDownloadParam(url), token);
		const blobUrl = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = blobUrl;
		link.download = filename || "tenancy_agreement.pdf";
		document.body.appendChild(link);
		link.click();
		link.remove();
		setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
		return { success: true };
	} catch (error) {
		return { success: false, message: pdfErrorMessage(error, "Failed to download PDF.") };
	}
};
